/**
 * Company Authentication Service
 * Handles company registration, login, and profile management
 */

import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  updateProfile 
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from '../../../services/firebase/firebase';

/**
 * Register a new company account
 * 
 * @param {Object} companyData - Company registration data
 * @returns {Promise<Object>} Company user and profile data
 */
export const registerCompany = async (companyData) => {
  try {
    // Create user with Firebase auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      companyData.email,
      companyData.password
    );

    // Update user profile with company name
    await updateProfile(userCredential.user, {
      displayName: companyData.companyName,
      photoURL: companyData.logoUrl || null
    });

    // Create company profile in Firestore
    const companyProfile = {
      companyName: companyData.companyName,
      email: companyData.email,
      contactPerson: companyData.contactPerson,
      industry: companyData.industry,
      companySize: companyData.companySize,
      website: companyData.website,
      location: companyData.location,
      description: companyData.description,
      logoUrl: companyData.logoUrl || null,
      verified: false, // Companies need verification
      adminUsers: [userCredential.user.uid],
      accountType: 'company',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'companyProfiles', userCredential.user.uid), companyProfile);

    // Also create an entry in employers collection for job posting permissions
    const employerData = {
      companyId: userCredential.user.uid,
      companyName: companyData.companyName,
      email: companyData.email,
      verified: false,
      createdAt: serverTimestamp()
    };

    await setDoc(doc(db, 'employers', userCredential.user.uid), employerData);

    return {
      user: userCredential.user,
      companyProfile
    };
  } catch (error) {
    console.error('Error registering company:', error);
    throw error;
  }
};

/**
 * Login company user
 * 
 * @param {string} email - Company email
 * @param {string} password - Company password
 * @returns {Promise<Object>} User credential and company profile
 */
export const loginCompany = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Get company profile
    const companyProfile = await getCompanyProfile(userCredential.user.uid);
    
    if (!companyProfile) {
      throw new Error('Company profile not found');
    }

    return {
      user: userCredential.user,
      companyProfile
    };
  } catch (error) {
    console.error('Error logging in company:', error);
    throw error;
  }
};

/**
 * Get company profile by user ID
 * 
 * @param {string} userId - Company user ID
 * @returns {Promise<Object|null>} Company profile or null
 */
export const getCompanyProfile = async (userId) => {
  try {
    const companyRef = doc(db, 'companyProfiles', userId);
    const companySnap = await getDoc(companyRef);
    
    if (companySnap.exists()) {
      return {
        id: companySnap.id,
        ...companySnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting company profile:', error);
    throw error;
  }
};

/**
 * Update company profile
 * 
 * @param {string} userId - Company user ID
 * @param {Object} updates - Profile updates
 * @returns {Promise<void>}
 */
export const updateCompanyProfile = async (userId, updates) => {
  try {
    const companyRef = doc(db, 'companyProfiles', userId);
    await updateDoc(companyRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    // Also update employer record if company name changed
    if (updates.companyName) {
      const employerRef = doc(db, 'employers', userId);
      await updateDoc(employerRef, {
        companyName: updates.companyName,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating company profile:', error);
    throw error;
  }
};

/**
 * Check if company name is available
 * 
 * @param {string} companyName - Company name to check
 * @returns {Promise<boolean>} True if available
 */
export const isCompanyNameAvailable = async (companyName) => {
  try {
    const companiesQuery = query(
      collection(db, 'companyProfiles'),
      where('companyName', '==', companyName)
    );
    
    const snapshot = await getDocs(companiesQuery);
    return snapshot.empty;
  } catch (error) {
    console.error('Error checking company name availability:', error);
    throw error;
  }
};

/**
 * Get company statistics
 * 
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Company statistics
 */
export const getCompanyStats = async (companyId) => {
  try {
    // Get job postings count
    const jobsQuery = query(
      collection(db, 'jobPostings'),
      where('employerId', '==', companyId)
    );
    const jobsSnapshot = await getDocs(jobsQuery);
    
    // Get applications count
    const applicationsQuery = query(
      collection(db, 'jobApplications'),
      where('jobId', 'in', jobsSnapshot.docs.map(doc => doc.id))
    );
    const applicationsSnapshot = await getDocs(applicationsQuery);
    
    // Calculate stats
    const activeJobs = jobsSnapshot.docs.filter(doc => 
      doc.data().status === 'active'
    ).length;
    
    const pendingApplications = applicationsSnapshot.docs.filter(doc => 
      doc.data().status === 'pending'
    ).length;
    
    return {
      totalJobs: jobsSnapshot.size,
      activeJobs,
      totalApplications: applicationsSnapshot.size,
      pendingApplications
    };
  } catch (error) {
    console.error('Error getting company stats:', error);
    throw error;
  }
};

export default {
  registerCompany,
  loginCompany,
  getCompanyProfile,
  updateCompanyProfile,
  isCompanyNameAvailable,
  getCompanyStats
};